import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton, Pagination, SearchInput } from "@/hq/HQPrimitives";

export default function HQBusinesses() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    const params = { page, page_size: 25 };
    if (q) params.q = q;
    hqApi.get("/businesses", { params })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr("Something went wrong loading businesses."));
    return () => { alive = false; };
  }, [q, page]);

  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="hq-businesses">
      <Card className="p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search name / domain / email…" testId="hq-businesses-search" />
          <div className="ml-auto text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A]" data-testid="hq-businesses-total">
            {data ? `${data.total} business${data.total === 1 ? "" : "es"}` : "…"}
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {!data ? (
          <div className="p-6 space-y-3">{[0,1,2].map(i => <Skeleton key={i} h={40} />)}</div>
        ) : items.length === 0 ? (
          <EmptyState title="No businesses yet" hint="Businesses will appear here as enquiries come in." testId="hq-businesses-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">
                <tr>
                  <th className="text-left px-5 py-3 font-bold">Business</th>
                  <th className="text-left px-5 py-3 font-bold">Industry</th>
                  <th className="text-left px-5 py-3 font-bold">Contact</th>
                  <th className="text-left px-5 py-3 font-bold">Website</th>
                  <th className="text-left px-5 py-3 font-bold">Services</th>
                  <th className="text-left px-5 py-3 font-bold">Leads</th>
                  <th className="text-left px-5 py-3 font-bold">Created</th>
                </tr>
              </thead>
              <tbody>
                {items.map((b) => (
                  <tr key={b.id} className="border-t border-[#EAEAEA] hover:bg-[#FFFDF6]" data-testid={`hq-business-row-${b.id}`}>
                    <td className="px-5 py-3">
                      <Link to={`/hq/businesses/${b.id}`} className="font-bold text-[#0A0A0A] hover:text-[#FF6A00]">{b.name}</Link>
                      {b.status && b.status !== "prospect" && (
                        <div className="mt-0.5 text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A]">{b.status}</div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-[#4A4A4A]">{b.industry || "—"}</td>
                    <td className="px-5 py-3 text-[#4A4A4A]">
                      {b.primary_contact?.full_name || b.primary_contact?.email || "—"}
                    </td>
                    <td className="px-5 py-3 text-[13px] text-[#4A4A4A] break-all">
                      {b.primary_website?.normalized_domain || "—"}
                    </td>
                    <td className="px-5 py-3 text-[12px] text-[#4A4A4A]">
                      {b.services?.length
                        ? b.services.map((s) => s.service?.name).filter(Boolean).slice(0,2).join(" · ")
                        : "—"}
                    </td>
                    <td className="px-5 py-3 font-black">{b.lead_count ?? 0}</td>
                    <td className="px-5 py-3 text-[12px] text-[#8A8A8A]">
                      {b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data && <Pagination page={data.page} total_pages={data.total_pages} onPage={setPage} testIdPrefix="hq-businesses-page" />}
    </div>
  );
}
